import React from 'react';
import {Box} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import {NAVY, TEXTE_SECONDAIRE} from '../../tokens.js';
import {constatAccessibilite, constatDetours, constatEcartVolPied, constatMaillage, constatNonRattaches, constatUsage, libelleDistance, ligneHorsParc} from '../../selectors/population.js';
import CarteAnalyse from './CarteAnalyse.js';
import NuagePoints from './NuagePoints.js';
import PetitTableau from './PetitTableau.js';

// Lot 13 : « Vol d'oiseau ou à pied ». Lot 12 : cartes « Accessibilité par commune » (réel, tous groupes), « Redondance et pression » (parc aérien, population réelle,
// tonnage simulé) et « Carreaux denses non desservis » (réel, ex-« Trous de maillage »).

const nb = (n, d = 0) => n.toLocaleString('fr-FR', {maximumFractionDigits: d});

function Titre({children}) {
    return <Box component="h3" sx={{m: '0 0 4px', fontSize: 12, fontWeight: 700, color: TEXTE_SECONDAIRE}}>{children}</Box>;
}

/** `resultat` = accessibiliteParCommune(...) ; `lienCommune(nom)` : carte centrée sur la commune, densité active. */
export function CarteAccessibilite({resultat, lienCommune, lienCarte}) {
    const pct = (x) => (x == null ? '—' : `${nb(x * 100)} %`);
    const ligne = (c, nom) => ({
        cle: c.nom,
        commune: nom,
        habitants: nb(c.habitants),
        p250: pct(c.part250),
        p100: pct(c.part100),
        pour1000: c.bornesPour1000 == null ? '—' : nb(c.bornesPour1000, 1),
        mediane: c.distanceMediane == null ? '—' : `${nb(c.distanceMediane)} m`,
    });
    const t = resultat.territoire;
    const lignes = [
        ligne({...t, nom: 'Territoire'}, <Box component="span" sx={{fontWeight: 700}}>Territoire</Box>),
        ...resultat.communes.map((c) => ligne(c, <Link to={lienCommune(c.nom)} style={{color: NAVY, fontWeight: 700, textDecoration: 'none'}}>{c.nom}</Link>)),
    ];
    return (
        <CarteAnalyse id="analyse-accessibilite" titre="Accessibilité par commune" source="reel" constat={constatAccessibilite(resultat)} lienCarte={lienCarte} pleineLargeur>
            <PetitTableau
                colonnes={[
                    {cle: 'commune', libelle: 'Commune'},
                    {cle: 'habitants', libelle: 'Habitants', droite: true},
                    {cle: 'p250', libelle: '< 250 m', droite: true},
                    {cle: 'p100', libelle: '< 100 m', droite: true},
                    {cle: 'pour1000', libelle: 'Bornes / 1 000 hab.', droite: true},
                    {cle: 'mediane', libelle: 'Distance médiane', droite: true},
                ]}
                lignes={lignes}
                legende="Communes équipées, de la moins à la mieux desservie (part des habitants à moins de 250 m à vol d'oiseau d'une borne active, tous groupes)"
            />
        </CarteAnalyse>
    );
}

/** `resultat` = maillageEtUsage(index), parc aérien seulement. Points et noms cliquables : ouvrent la fiche du PAV. */
export function CarteUsage({resultat, slug, lienCarte}) {
    const navigate = useNavigate();
    const {nuage, zoneReduite, sousPression, seuils} = resultat;
    const accents = new Set(sousPression.map((d) => d.pavId));
    const points = nuage.points.map((p) => ({
        id: p.pavId, x: p.pop, y: p.kgMois, accent: accents.has(p.pavId), titre: `${p.nomenclature} : ${nb(p.pop)} hab. alloués, ${nb(p.kgMois)} kg par mois`,
    }));
    const lien = (d) => <Link to={`/pilotage/${slug}/pav/${d.pavId}`} style={{color: NAVY, fontWeight: 700, textDecoration: 'none'}}>{d.nomenclature}</Link>;
    const kg = (d) => (d.kgSemaine == null ? '—' : nb(d.kgSemaine, 1));
    const lignesReduites = zoneReduite.slice(0, 8).map((d) => ({cle: d.pavId, pav: lien(d), commune: d.commune, pop: nb(d.populationAllouee), autres: nb(d.nbBornesAPortee), kg: kg(d)}));
    const lignesPression = sousPression.slice(0, 8).map((d) => ({cle: d.pavId, pav: lien(d), commune: d.commune, pop: nb(d.populationAllouee), taux: `${nb(d.remplissage * 100)} %`, kg: kg(d)}));
    const r = nuage.r;
    return (
        <CarteAnalyse id="analyse-usage" titre="Redondance et pression (parc aérien)" source="mixte" constat={constatUsage(resultat)} lienCarte={lienCarte} pleineLargeur>
            <Box sx={{display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: '24px', alignItems: 'start'}}>
                <Box>
                    <NuagePoints
                        points={points}
                        libelleX="Population allouée (habitants)"
                        libelleY="Tonnage mensuel moyen (kg)"
                        seuilX={seuils.pressionHab}
                        onPoint={(id) => navigate(`/pilotage/${slug}/pav/${id}`)}
                        aria={`Nuage de ${points.length} bornes aériennes : population allouée et tonnage mensuel moyen`}
                    />
                    <Box component="p" sx={{m: '4px 0 0', fontSize: 12, color: TEXTE_SECONDAIRE}}>
                        {r === null ? 'Corrélation population × tonnage non calculable' : `Corrélation population allouée × tonnage : r = ${nb(r, 2)}`} · {nb(points.length)} bornes aériennes
                    </Box>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0}}>
                    <Box>
                        <Titre>Bornes à zone propre réduite ({nb(zoneReduite.length)})</Titre>
                        {lignesReduites.length > 0 ? (
                            <PetitTableau
                                colonnes={[{cle: 'pav', libelle: 'PAV'}, {cle: 'commune', libelle: 'Commune'}, {cle: 'pop', libelle: 'Hab. alloués', droite: true}, {cle: 'autres', libelle: 'Bornes à portée', droite: true}, {cle: 'kg', libelle: 'kg / semaine', droite: true}]}
                                lignes={lignesReduites}
                                legende={`Les ${lignesReduites.length} bornes à zone propre réduite les moins peuplées`}
                            />
                        ) : <Box sx={{fontSize: 12, color: TEXTE_SECONDAIRE}}>Aucune</Box>}
                    </Box>
                    <Box>
                        <Titre>Bornes sous pression ({nb(sousPression.length)})</Titre>
                        {lignesPression.length > 0 ? (
                            <PetitTableau
                                colonnes={[{cle: 'pav', libelle: 'PAV'}, {cle: 'commune', libelle: 'Commune'}, {cle: 'pop', libelle: 'Hab. alloués', droite: true}, {cle: 'taux', libelle: 'Remplissage', droite: true}, {cle: 'kg', libelle: 'kg / semaine', droite: true}]}
                                lignes={lignesPression}
                                legende={`Les ${lignesPression.length} bornes sous pression les plus peuplées`}
                            />
                        ) : <Box sx={{fontSize: 12, color: TEXTE_SECONDAIRE}}>Aucune</Box>}
                    </Box>
                </Box>
            </Box>
        </CarteAnalyse>
    );
}

/**
 * Lot 13 : « Vol d'oiseau ou à pied ». `resultat` = ecartVolOiseauPied(...) ; par commune équipée, part des habitants à moins de 150 m
 * d'une borne active à vol d'oiseau puis à pied, écart en points, tri par écart décroissant. Sans distances à pied : état explicite.
 */
export function CarteVolPied({resultat, lienCommune, nonRattaches = null}) {
    const titre = "Vol d'oiseau ou à pied";
    if (!resultat.disponible) {
        return (
            <CarteAnalyse id="analyse-vol-pied" titre={titre} source="reel" constat={constatEcartVolPied(resultat)} lienCarte={null}>
                <Box role="status" sx={{fontSize: 13, color: TEXTE_SECONDAIRE}}>Distances à pied non disponibles</Box>
            </CarteAnalyse>
        );
    }
    const pct = (x) => (x == null ? '—' : `${nb(x * 100)} %`);
    const ligne = (c, nom) => ({cle: c.nom, commune: nom, habitants: nb(c.habitants), vol: pct(c.partVol), pied: pct(c.partPied), ecart: c.ecart == null ? '—' : nb(c.ecart, 1)});
    const lignes = [
        ligne({...resultat.territoire, nom: 'Territoire'}, <Box component="span" sx={{fontWeight: 700}}>Territoire</Box>),
        ...resultat.communes.map((c) => ligne(c, <Link to={lienCommune(c.nom)} style={{color: NAVY, fontWeight: 700, textDecoration: 'none'}}>{c.nom}</Link>)),
    ];
    const tete = resultat.communes[0];
    const ligneNR = nonRattaches ? constatNonRattaches(nonRattaches) : null;
    return (
        <CarteAnalyse id="analyse-vol-pied" titre={titre} source="reel" constat={constatEcartVolPied(resultat)} lienCarte={tete ? lienCommune(tete.nom) : null} pleineLargeur>
            <PetitTableau
                colonnes={[
                    {cle: 'commune', libelle: 'Commune'},
                    {cle: 'habitants', libelle: 'Habitants', droite: true},
                    {cle: 'vol', libelle: `< ${resultat.seuilM} m à vol d'oiseau`, droite: true},
                    {cle: 'pied', libelle: `< ${resultat.seuilM} m à pied`, droite: true},
                    {cle: 'ecart', libelle: 'Écart (points)', droite: true},
                ]}
                lignes={lignes}
                legende={`Communes équipées, par écart décroissant (part des habitants à moins de ${resultat.seuilM} m d'une borne active, tous groupes)`}
            />
            {ligneNR && <Box component="p" data-testid="non-rattaches" sx={{m: '8px 0 0', fontSize: 12, color: TEXTE_SECONDAIRE}}>{ligneNR}</Box>}
        </CarteAnalyse>
    );
}

/** `resultat` = trousDeMaillage(...) ; `lienCommune(nom)` : lien carte centré sur la commune, densité active. */
export function CarteMaillage({resultat, lienCommune, chalandise = null}) {
    const lignes = resultat.parCommune.slice(0, 8).map((c) => ({
        cle: c.nom,
        commune: <Link to={lienCommune(c.nom)} style={{color: NAVY, fontWeight: 700, textDecoration: 'none'}}>{c.nom}</Link>,
        nb: nb(c.nb),
    }));
    const tete = resultat.parCommune[0];
    const horsParc = ligneHorsParc(resultat);
    return (
        <CarteAnalyse id="analyse-maillage" titre="Carreaux denses non desservis" source="reel" constat={constatMaillage(resultat, {chalandise})} lienCarte={tete ? lienCommune(tete.nom) : null}>
            {horsParc && <Box component="p" sx={{m: '0 0 8px', fontSize: 12, color: TEXTE_SECONDAIRE}}>{horsParc}</Box>}
            {lignes.length > 0 && (
                <PetitTableau
                    colonnes={[{cle: 'commune', libelle: 'Commune (8 premières)'}, {cle: 'nb', libelle: 'Carreaux', droite: true}]}
                    lignes={lignes}
                    legende={`Carreaux de plus de 150 habitants sans borne active à moins de 400 m ${libelleDistance(chalandise)}, par commune équipée`}
                />
            )}
        </CarteAnalyse>
    );
}

/**
 * Lot 13 (D8) : « Détours à pied ». `resultat` = detoursAPied(index, communes), `null` si les distances à pied manquent.
 * Total, tableau par commune (habitants décroissants), 10 premiers carreaux avec leur borne la plus proche à vol d'oiseau.
 */
export function CarteDetours({resultat, slug, lienCommune}) {
    const titre = 'Détours à pied';
    if (resultat === null) {
        return (
            <CarteAnalyse id="analyse-detours" titre={titre} source="reel" constat={constatDetours(resultat)} lienCarte={null}>
                <Box role="status" sx={{fontSize: 13, color: TEXTE_SECONDAIRE}}>Distances à pied non disponibles</Box>
            </CarteAnalyse>
        );
    }
    const communes = resultat.parCommune.map((c) => ({cle: c.commune, commune: <Link to={lienCommune(c.commune)} style={{color: NAVY, fontWeight: 700, textDecoration: 'none'}}>{c.commune}</Link>, carreaux: nb(c.carreaux), habitants: nb(c.habitants)}));
    const carreaux = resultat.carreaux.slice(0, 10).map((c) => ({
        cle: c.index,
        carreau: `Carreau ${c.index}`,
        commune: c.commune,
        pop: nb(c.pop),
        pav: <Link to={`/pilotage/${slug}/pav/${c.pavProche.id}`} style={{color: NAVY, fontWeight: 700, textDecoration: 'none'}}>{c.pavProche.nomenclature}</Link>,
        dist: `${nb(c.pavProche.distanceM)} m`,
    }));
    const tete = resultat.parCommune[0];
    return (
        <CarteAnalyse id="analyse-detours" titre={titre} source="reel" constat={constatDetours(resultat)} lienCarte={tete ? lienCommune(tete.commune) : null} pleineLargeur>
            <Box component="p" data-testid="detours-total" sx={{m: '0 0 8px', fontSize: 12, color: TEXTE_SECONDAIRE}}>
                {nb(resultat.total.carreaux)} carreau{resultat.total.carreaux > 1 ? 'x' : ''} · {nb(resultat.total.habitants)} habitants
            </Box>
            {communes.length > 0 && (
                <Box sx={{display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)', gap: '24px', alignItems: 'start'}}>
                    <PetitTableau
                        colonnes={[{cle: 'commune', libelle: 'Commune'}, {cle: 'carreaux', libelle: 'Carreaux', droite: true}, {cle: 'habitants', libelle: 'Habitants', droite: true}]}
                        lignes={communes}
                        legende="Par commune, du plus au moins d'habitants concernés"
                    />
                    <PetitTableau
                        colonnes={[{cle: 'carreau', libelle: 'Carreau'}, {cle: 'commune', libelle: 'Commune'}, {cle: 'pop', libelle: 'Habitants', droite: true}, {cle: 'pav', libelle: 'Borne la plus proche'}, {cle: 'dist', libelle: "À vol d'oiseau", droite: true}]}
                        lignes={carreaux}
                        legende="Les 10 carreaux les plus peuplés : borne à moins de 300 m à vol d'oiseau, aucune à moins de 400 m à pied (détour à pied)"
                    />
                </Box>
            )}
        </CarteAnalyse>
    );
}
